import React, { useState } from 'react';
import { Alert, Card, Descriptions, Input, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import api from '../../services/api';

const { Title, Text } = Typography;
const dateText = (value) => value ? new Date(value).toLocaleString('zh-CN') : '—';
const badge = (value) => <Tag color={value === 'active' ? 'green' : value === 'disabled' ? 'red' : 'default'}>{value || '—'}</Tag>;
const roleText = (role) => ({ owner: '班主任／负责人', creator: '班主任／负责人', head_teacher: '班主任／负责人', teacher: '协助教师', school_admin: '学校管理员', platform_admin: '平台管理员', parent: '家长' }[role] || role || '—');
const jsonText = (value) => { try { return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2); } catch { return String(value || '—'); } };

export default function TeacherCodeQuery() {
  const [code, setCode] = useState('');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async (value) => {
    const normalized = String(value || '').trim().toUpperCase();
    if (!/^T-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(normalized)) return message.warning('请输入完整教师码，例如 T-A1JC-FEXX');
    setLoading(true);
    setRecord(null);
    try {
      const response = await api.get('/teacher-code-query/lookup', { params: { code: normalized } });
      setRecord(response.data.data);
      setCode(normalized);
    } catch (error) { message.error(error?.response?.data?.message || '查询失败，请重试'); }
    finally { setLoading(false); }
  };
  const teacher = record?.teacher;
  const classes = record?.classes || [];
  const active = classes.filter((item) => item.class_status === 'active' && item.membership_status === 'active');
  const ownerCount = active.filter((item) => ['owner', 'creator', 'head_teacher'].includes(item.role)).length;
  const classOwners = (classId) => (record?.owners || []).filter((item) => String(item.class_id) === String(classId));
  return <div style={{ maxWidth: 1500, margin: '0 auto' }}>
    <Title level={3}>教师码查询</Title>
    <Alert type="info" showIcon message="只读查询：仅平台管理员可查看，不提供转让、停用、修改或删除操作。" />
    <Card style={{ marginTop: 16, marginBottom: 16 }}>
      <Input.Search value={code} onChange={(event) => setCode(event.target.value)} onSearch={search}
        placeholder="教师码，例如 T-A1JC-FEXX" enterButton="查询教师" loading={loading} style={{ maxWidth: 480 }} />
    </Card>
    {record && <>
      <Card style={{ marginBottom: 16 }}>
        <Descriptions title="教师资料与状态" bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
          <Descriptions.Item label="教师码">{teacher.public_code} {badge(teacher.code_status)}</Descriptions.Item>
          <Descriptions.Item label="账号 ID">{teacher.account_id}</Descriptions.Item>
          <Descriptions.Item label="账号状态">{badge(teacher.account_status)}</Descriptions.Item>
          <Descriptions.Item label="姓名">{teacher.display_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="用户名">{teacher.username || '—'}</Descriptions.Item>
          <Descriptions.Item label="手机号">{teacher.phone || '—'}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{teacher.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="注册时间">{dateText(teacher.created_at)}</Descriptions.Item>
          <Descriptions.Item label="最近资料更新">{dateText(teacher.updated_at)}</Descriptions.Item>
          <Descriptions.Item label="有效班级">{active.length}</Descriptions.Item>
          <Descriptions.Item label="担任负责人">{ownerCount}</Descriptions.Item>
          <Descriptions.Item label="担任协助教师">{active.length - ownerCount}</Descriptions.Item>
        </Descriptions>
      </Card>
      {record.classesTruncated && <Alert type="warning" message="班级关系超过 500 条，当前只展示最近 500 条，统计也仅基于已展示记录。" />}
      <Tabs items={[
        { key: 'classes', label: `班级关系（${classes.length}）`, children: <Table rowKey="membership_id" size="small" scroll={{ x: 1150 }} dataSource={classes} pagination={{ pageSize: 20 }} columns={[
          { title: '班级 ID', dataIndex: 'class_id', width: 95 },
          { title: '年级', dataIndex: 'grade', width: 85 }, { title: '班级', dataIndex: 'class_name' },
          { title: '学校 ID', dataIndex: 'school_id', width: 100 }, { title: '学校', dataIndex: 'school_name' },
          { title: '班级状态', dataIndex: 'class_status', render: badge },
          { title: '该教师角色', dataIndex: 'role', render: roleText },
          { title: '教师关系状态', dataIndex: 'membership_status', render: badge },
          { title: '当前班主任／负责人', key: 'owners', width: 240, render: (_, item) => {
            const owners = classOwners(item.class_id);
            return owners.length ? <Space direction="vertical">{owners.map((owner) => <Text key={owner.account_id}>{owner.display_name || owner.username || owner.account_id}<br /><Text type="secondary">{owner.public_code || `账号 ${owner.account_id}`}</Text></Text>)}</Space> : <Tag color="red">暂无负责人</Tag>;
          } }
        ]} /> },
        { key: 'schools', label: '学校关系', children: <Table rowKey="id" size="small" scroll={{ x: 700 }} dataSource={record.schools || []} pagination={{ pageSize: 20 }} columns={[
          { title: '学校 ID', dataIndex: 'school_id' }, { title: '学校', dataIndex: 'school_name' },
          { title: '身份', dataIndex: 'role', render: roleText }, { title: '状态', dataIndex: 'status', render: badge },
          { title: '加入时间', dataIndex: 'created_at', render: dateText }
        ]} /> },
        { key: 'login', label: '登录与身份', children: <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="展示最近 20 条会话和身份记录。IP 相同不能单独证明为同一个人；没有 IP 记录也不代表没有登录。" />
          <Table rowKey={(item, index) => `${item.provider}-${index}`} size="small" pagination={false} dataSource={record.identities || []} columns={[
            { title: '登录身份类型', dataIndex: 'provider' }, { title: '状态', dataIndex: 'status', render: badge }, { title: '创建时间', dataIndex: 'created_at', render: dateText }
          ]} />
          <Table rowKey="id" size="small" scroll={{ x: 950 }} dataSource={record.sessions || []} columns={[
            { title: '客户端', dataIndex: 'client_type' }, { title: '设备 ID', dataIndex: 'device_id' }, { title: 'IP', dataIndex: 'ip_address' },
            { title: '状态', dataIndex: 'status', render: badge }, { title: '会话创建', dataIndex: 'created_at', render: dateText },
            { title: '会话更新', dataIndex: 'updated_at', render: dateText }, { title: 'User-Agent', dataIndex: 'user_agent', ellipsis: true }
          ]} />
        </Space> },
        { key: 'history', label: '班级历史与审计', children: <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="展示这些班级最近 60 条相关业务记录，不代表均由该教师操作；请核对记录中的操作账号及转出、转入账号。" />
          <Table rowKey="id" size="small" scroll={{ x: 1150 }} dataSource={record.history || []} columns={[
            { title: '时间', dataIndex: 'created_at', render: dateText }, { title: '类型', dataIndex: 'module_name' },
            { title: '班级 ID', dataIndex: 'class_id' }, { title: '操作账号', dataIndex: 'created_by' },
            { title: '转出账号', dataIndex: 'from_account_id' }, { title: '转入账号', dataIndex: 'to_account_id' },
            { title: '标题', dataIndex: 'title' }, { title: '内容', dataIndex: 'content', ellipsis: true }
          ]} />
          <Title level={5}>教师码管理审计记录</Title>
          <Table rowKey="id" size="small" scroll={{ x: 1000 }} dataSource={record.audits || []} columns={[
            { title: '时间', dataIndex: 'created_at', render: dateText }, { title: '操作者账号', dataIndex: 'actor_account_id' }, { title: '操作', dataIndex: 'action' },
            { title: '操作前', dataIndex: 'before_value', render: (value) => <pre style={{ whiteSpace: 'pre-wrap' }}>{jsonText(value)}</pre> },
            { title: '操作后', dataIndex: 'after_value', render: (value) => <pre style={{ whiteSpace: 'pre-wrap' }}>{jsonText(value)}</pre> }
          ]} />
        </Space> }
      ]} />
    </>}
  </div>;
}
